import { useEffect } from 'react';
import { Mail, Github, Linkedin, Youtube, X } from 'lucide-react';
import { useFocusTrap } from '../hooks/useFocusTrap';
import { NAV_LINKS, SOCIAL } from '../data/site';
import SectionLink from './SectionLink';
import ResumeLink from './ResumeLink';
import { trackEvent } from '../utils/analytics';

const ICONS = { LinkedIn: Linkedin, GitHub: Github, Email: Mail, YouTube: Youtube };

export default function MobileMenu({ open, onClose, activeId }) {
  const trapRef = useFocusTrap(open);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      ref={trapRef}
      id="mobile-menu"
      className="fixed inset-0 z-[55] bg-void flex flex-col pt-24 px-6 md:hidden"
      role="dialog"
      aria-modal="true"
      aria-label="Mobile navigation"
    >
      <button
        type="button"
        onClick={onClose}
        className="absolute top-4 right-4 p-2 text-muted hover:text-ink"
        aria-label="Close menu"
      >
        <X size={22} />
      </button>

      <nav aria-label="Mobile navigation links">
        {NAV_LINKS.map((l, i) => (
          <SectionLink
            key={l.href}
            href={l.href}
            onNavigate={onClose}
            className={`rise flex items-baseline gap-4 py-3 border-b border-line ${
              activeId === l.id ? 'text-ink' : 'text-muted'
            }`}
            style={{ '--d': `${i * 60}ms` }}
          >
            <span className="font-mono text-xs text-faint">0{i + 1}</span>
            <span className="display text-4xl">{l.label}</span>
          </SectionLink>
        ))}
      </nav>

      <ResumeLink onClick={onClose} className="btn-primary mt-8 self-start">
        Download resume
      </ResumeLink>

      <div className="mt-auto pb-10 flex gap-2">
        {SOCIAL.filter((s) => ICONS[s.label]).map(({ label, href }) => {
          const Icon = ICONS[label];
          const external = href.startsWith('http');
          return (
            <a
              key={label}
              href={href}
              target={external ? '_blank' : undefined}
              rel={external ? 'noopener noreferrer' : undefined}
              aria-label={label}
              className="h-11 w-11 rounded-full border border-line flex items-center justify-center text-muted hover:text-ink hover:border-ink transition-colors"
              onClick={() => trackEvent('contact_click', { method: label, source: 'mobile_menu' })}
            >
              <Icon size={18} />
            </a>
          );
        })}
      </div>
    </div>
  );
}
